import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Settings2,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

// Predefined colors for categories
const COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#f97316", "#14b8a6", "#6366f1",
  "#84cc16", "#a855f7", "#0ea5e9", "#10b981", "#f43f5e",
];

// Default templates for each category type
const DEFAULT_TEMPLATES = {
  equipment: [
    { name: "Power Tools", color: "#f59e0b" },
    { name: "Hand Tools", color: "#3b82f6" },
    { name: "Cleaning Equipment", color: "#22c55e" },
    { name: "Safety Gear", color: "#ef4444" },
    { name: "Vehicles", color: "#8b5cf6" },
  ],
  vehicle: [
    { name: "Truck", color: "#3b82f6" },
    { name: "Van", color: "#22c55e" },
    { name: "Car", color: "#f59e0b" },
    { name: "Trailer", color: "#8b5cf6" },
  ],
  training: [
    { name: "Safety", color: "#ef4444" },
    { name: "Onboarding", color: "#22c55e" },
    { name: "Compliance", color: "#f59e0b" },
    { name: "Skills Development", color: "#3b82f6" },
  ],
  document: [
    { name: "Invoice", color: "#22c55e" },
    { name: "Quote", color: "#3b82f6" },
    { name: "Work Order", color: "#f59e0b" },
    { name: "Contract", color: "#8b5cf6" },
  ],
  safety: [
    { name: "Hazard Assessment", color: "#ef4444" },
    { name: "PPE Requirements", color: "#f59e0b" },
    { name: "Emergency Procedures", color: "#ec4899" },
    { name: "Equipment Safety", color: "#3b82f6" },
  ],
  certification: [
    { name: "First Aid", color: "#ef4444" },
    { name: "Safety Training", color: "#f59e0b" },
    { name: "Equipment License", color: "#3b82f6" },
    { name: "Professional Certification", color: "#8b5cf6" },
  ],
  policy: [
    { name: "HR Policy", color: "#3b82f6" },
    { name: "Safety Policy", color: "#ef4444" },
    { name: "Operations", color: "#22c55e" },
    { name: "Compliance", color: "#f59e0b" },
  ],
  procedure: [
    { name: "Standard Operating Procedure", color: "#3b82f6" },
    { name: "Emergency Response", color: "#ef4444" },
    { name: "Quality Control", color: "#22c55e" },
    { name: "Workflow", color: "#f59e0b" },
  ],
  service: [
    { name: "Cleaning", color: "#22c55e" },
    { name: "Maintenance", color: "#3b82f6" },
    { name: "Installation", color: "#f59e0b" },
    { name: "Consultation", color: "#8b5cf6" },
  ],
  goods: [
    { name: "Supplies", color: "#3b82f6" },
    { name: "Materials", color: "#22c55e" },
    { name: "Parts", color: "#f59e0b" },
    { name: "Equipment", color: "#8b5cf6" },
  ],
  unit: [
    { name: "Per Hour", color: "#3b82f6" },
    { name: "Per Window", color: "#22c55e" },
    { name: "Per Square Foot", color: "#f59e0b" },
    { name: "Per Unit", color: "#8b5cf6" },
    { name: "Flat Rate", color: "#06b6d4" },
    { name: "Per Day", color: "#ec4899" },
  ],
  job_type: [
    { name: "Window Cleaning", color: "#22c55e" },
    { name: "General Maintenance", color: "#3b82f6" },
    { name: "Emergency Repair", color: "#ef4444" },
    { name: "Site Inspection", color: "#f59e0b" },
    { name: "Installation", color: "#8b5cf6" },
    { name: "Consultation", color: "#06b6d4" },
  ],
  terms: [
    { name: "Terms of Service", color: "#3b82f6" },
    { name: "Privacy Policy", color: "#22c55e" },
    { name: "Liability Waiver", color: "#ef4444" },
    { name: "Service Agreement", color: "#f59e0b" },
  ],
  expense: [
    { name: "Supplies", color: "#3b82f6" },
    { name: "Equipment", color: "#8b5cf6" },
    { name: "Fleet", color: "#22c55e" },
    { name: "Utilities", color: "#f59e0b" },
    { name: "Insurance", color: "#06b6d4" },
  ],
  client: [
    { name: "Residential", color: "#22c55e" },
    { name: "Commercial", color: "#3b82f6" },
    { name: "Industrial", color: "#8b5cf6" },
    { name: "Government", color: "#f59e0b" },
  ],
  company: [
    { name: "Corporation", color: "#3b82f6" },
    { name: "LLC", color: "#22c55e" },
    { name: "Partnership", color: "#8b5cf6" },
    { name: "Sole Proprietor", color: "#f59e0b" },
    { name: "Non-Profit", color: "#06b6d4" },
  ],
  employee: [
    { name: "Full-Time", color: "#22c55e" },
    { name: "Part-Time", color: "#3b82f6" },
    { name: "Contract", color: "#f59e0b" },
    { name: "Seasonal", color: "#8b5cf6" },
  ],
  job: [
    { name: "Recurring", color: "#22c55e" },
    { name: "One-Time", color: "#3b82f6" },
    { name: "Emergency", color: "#ef4444" },
    { name: "Maintenance", color: "#f59e0b" },
  ],
  quote_status: [
    { name: "Draft", color: "#6b7280" },
    { name: "Sent", color: "#3b82f6" },
    { name: "Viewed", color: "#06b6d4" },
    { name: "Approved", color: "#22c55e" },
    { name: "Rejected", color: "#ef4444" },
  ],
  portal_user: [
    { name: "Full Access", color: "#22c55e" },
    { name: "View Only", color: "#3b82f6" },
    { name: "Limited", color: "#f59e0b" },
  ],
};

/**
 * CategoryManager - Inline category management component
 * 
 * Props:
 * - categoryType: string - The type of category (e.g., "equipment", "document", "certification")
 * - label: string - Display label (e.g., "Equipment Categories", "Document Types")
 * - onCategoriesChange: function - Callback when categories change
 * - compact: boolean - Show as compact button (default: false)
 * - showLabel: boolean - Show "Manage" text (default: true for non-compact)
 * - showAsDropdown: boolean - Show as dropdown with "Manage" option (default: false)
 * - selectedValue: string - Currently selected value (for dropdown mode)
 * - onSelectionChange: function - Callback when selection changes (for dropdown mode)
 * - allowAll: boolean - Show "All" option in dropdown (default: false)
 */
export default function CategoryManager({ 
  categoryType, 
  label = "Categories",
  onCategoriesChange,
  compact = false,
  showLabel = true,
  showAsDropdown = false,
  selectedValue = "",
  onSelectionChange,
  allowAll = false,
  // Phase 14: optional controlled mode so any Select dropdown can open the manager directly
  controlledOpen,
  onControlledOpenChange,
}) {
  const [internalShowDialog, setInternalShowDialog] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const showDialog = isControlled ? controlledOpen : internalShowDialog;
  const setShowDialog = (v) => {
    if (isControlled) onControlledOpenChange?.(v);
    else setInternalShowDialog(v);
  };
  const [localCategories, setLocalCategories] = useState([]);
  const [dropdownCategories, setDropdownCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: "", color: COLORS[0] });
  const [editForm, setEditForm] = useState({ name: "", color: "" });
  const [loading, setLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Fetch categories for dropdown mode on mount
  useEffect(() => {
    if (showAsDropdown) {
      fetchDropdownCategories();
    }
  }, [showAsDropdown, categoryType]);

  useEffect(() => {
    if (showDialog) {
      fetchCategories();
    }
  }, [showDialog, categoryType]);

  const fetchDropdownCategories = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/categories/?category_type=${categoryType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const categories = Array.isArray(data) ? data : (data.categories || []);
        setDropdownCategories(categories);
      }
    } catch (err) {
      console.error("Failed to fetch categories for dropdown");
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/categories/?category_type=${categoryType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // API returns a list directly
        const categories = Array.isArray(data) ? data : (data.categories || []);
        setLocalCategories(categories);
        // Show templates suggestion if no categories exist
        if (categories.length === 0) {
          setShowTemplates(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch categories");
    }
  };

  const handleCreate = async (name = null, color = null) => {
    const categoryName = name || newCategory.name.trim();
    const categoryColor = color || newCategory.color;
    
    if (!categoryName) {
      toast.error("Name is required");
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/categories/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: categoryName,
          category_type: categoryType,
          color: categoryColor,
        }),
      });

      if (res.ok) {
        if (!name) {
          toast.success("Added successfully");
          setNewCategory({ name: "", color: COLORS[Math.floor(Math.random() * COLORS.length)] });
        }
        fetchCategories();
        if (onCategoriesChange) onCategoriesChange();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create");
      }
    } catch (err) {
      toast.error("Error creating category");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplates = async () => {
    const templates = DEFAULT_TEMPLATES[categoryType] || [];
    if (templates.length === 0) {
      toast.info("No templates available for this type");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");
    let added = 0;
    
    for (const template of templates) {
      try {
        const res = await fetch(`${API}/api/categories/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: template.name,
            category_type: categoryType,
            color: template.color,
          }),
        });
        if (res.ok) added++;
      } catch (err) {
        console.error("Failed to add template:", template.name);
      }
    }
    
    toast.success(`Added ${added} default ${label.toLowerCase()}`);
    setShowTemplates(false);
    setLoading(false);
    fetchCategories();
    if (onCategoriesChange) onCategoriesChange();
  };

  const handleUpdate = async (id) => {
    if (!editForm.name.trim()) {
      toast.error("Name is required");
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/categories/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          color: editForm.color,
        }),
      });

      if (res.ok) {
        toast.success("Updated successfully");
        setEditingId(null);
        fetchCategories();
        if (onCategoriesChange) onCategoriesChange();
      }
    } catch (err) {
      toast.error("Error updating");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Items using it may be affected.`)) return;

    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/categories/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success("Deleted successfully");
        fetchCategories();
        if (onCategoriesChange) onCategoriesChange();
      }
    } catch (err) {
      toast.error("Error deleting");
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, color: cat.color || COLORS[0] });
  };

  const ColorPicker = ({ value, onChange }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-10 h-8 p-0 border-2"
          style={{ backgroundColor: value, borderColor: value }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="grid grid-cols-5 gap-1">
          {COLORS.map((color) => (
            <button
              key={color}
              className="w-6 h-6 rounded border-2 transition-transform hover:scale-110"
              style={{ 
                backgroundColor: color,
                borderColor: value === color ? "#fff" : "transparent"
              }}
              onClick={() => onChange(color)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );

  const singularLabel = label.replace(/ies$/, 'y').replace(/s$/, '');

  // Handle dialog close and refresh dropdown categories
  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open && showAsDropdown) {
      fetchDropdownCategories();
    }
  };

  // Dropdown mode render
  if (showAsDropdown) {
    return (
      <>
        <Select 
          value={selectedValue || "all"} 
          onValueChange={(v) => {
            if (v === "__manage__") {
              setShowDialog(true);
            } else {
              onSelectionChange?.(v);
            }
          }}
        >
          <SelectTrigger className="w-48" data-testid={`${categoryType}-filter-dropdown`}>
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent>
            {allowAll && <SelectItem value="all">{label}</SelectItem>}
            {dropdownCategories.map((cat) => (
              <SelectItem key={cat.id} value={cat.name?.toLowerCase().replace(/\s+/g, '_') || cat.id}>
                <div className="flex items-center gap-2">
                  {cat.color && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />}
                  {cat.name}
                </div>
              </SelectItem>
            ))}
            <SelectItem value="__manage__" className="text-primary border-t mt-1 pt-1">
              <div className="flex items-center gap-2">
                <Settings2 className="w-3 h-3" />
                Manage {label}
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Manage {label}
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove {label.toLowerCase()} used throughout the app.
            </DialogDescription>
          </DialogHeader>

          {/* Quick Templates */}
          {showTemplates && DEFAULT_TEMPLATES[categoryType] && localCategories.length === 0 && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-sm">Quick Start</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add commonly used {label.toLowerCase()} with one click
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {DEFAULT_TEMPLATES[categoryType].slice(0, 4).map((t, i) => (
                      <Badge 
                        key={i} 
                        style={{ backgroundColor: t.color + "20", color: t.color, borderColor: t.color }}
                        className="text-xs border"
                      >
                        {t.name}
                      </Badge>
                    ))}
                    {DEFAULT_TEMPLATES[categoryType].length > 4 && (
                      <Badge variant="outline" className="text-xs">+{DEFAULT_TEMPLATES[categoryType].length - 4} more</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddTemplates} disabled={loading}>
                      <Sparkles className="w-3 h-3 mr-1" />
                      Add All Templates
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowTemplates(false)}>
                      Skip
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add new category */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground">New {singularLabel}</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder={`Enter ${singularLabel.toLowerCase()} name...`}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                data-testid={`new-${categoryType}-input`}
              />
            </div>
            <ColorPicker 
              value={newCategory.color} 
              onChange={(c) => setNewCategory({ ...newCategory, color: c })} 
            />
            <Button size="sm" onClick={() => handleCreate()} disabled={loading || !newCategory.name.trim()}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Categories list */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {localCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No {label.toLowerCase()} yet
              </p>
            ) : (
              localCategories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="flex items-center gap-2 p-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  data-testid={`category-${cat.id}`}
                >
                  {editingId === cat.id ? (
                    <>
                      <ColorPicker 
                        value={editForm.color} 
                        onChange={(c) => setEditForm({ ...editForm, color: c })} 
                      />
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="flex-1 h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleUpdate(cat.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleUpdate(cat.id)} className="h-8 w-8 p-0">
                        <Check className="w-4 h-4 text-green-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 w-8 p-0">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0 ring-2 ring-offset-1 ring-offset-background" 
                        style={{ backgroundColor: cat.color || "#6b7280", ringColor: cat.color || "#6b7280" }}
                      />
                      <span className="flex-1 text-sm font-medium">{cat.name}</span>
                      {cat.item_count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {cat.item_count} used
                        </Badge>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => startEdit(cat)} className="h-8 w-8 p-0 opacity-50 hover:opacity-100">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 opacity-50 hover:opacity-100 text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(cat.id, cat.name)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Show templates button if they dismissed it but have no categories */}
          {!showTemplates && localCategories.length === 0 && DEFAULT_TEMPLATES[categoryType] && (
            <Button variant="outline" size="sm" onClick={() => setShowTemplates(true)} className="w-full">
              <Sparkles className="w-4 h-4 mr-2" />
              Show Quick Start Templates
            </Button>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
  }

  // Button mode render (compact or full)
  return (
    <>
      {compact ? (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowDialog(true)}
          className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
          title={`Manage ${label}`}
          data-testid={`manage-${categoryType}-btn`}
        >
          <Settings2 className="w-4 h-4" />
        </Button>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowDialog(true)}
          data-testid={`manage-${categoryType}-btn`}
        >
          <Settings2 className="w-4 h-4 mr-2" />
          {showLabel ? `Manage ${label}` : label}
        </Button>
      )}

      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Manage {label}
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove {label.toLowerCase()} used throughout the app.
            </DialogDescription>
          </DialogHeader>

          {/* Quick Start Templates */}
          {showTemplates && DEFAULT_TEMPLATES[categoryType] && (
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">Quick Start</span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Add common {label.toLowerCase()} to get started quickly:
              </p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {DEFAULT_TEMPLATES[categoryType].map((t, i) => (
                  <Badge 
                    key={i} 
                    variant="outline" 
                    className="text-xs"
                    style={{ borderColor: t.color, color: t.color }}
                  >
                    {t.name}
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddTemplates} disabled={loading} className="flex-1">
                  {loading ? "Adding..." : "Add All"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowTemplates(false)}>
                  Skip
                </Button>
              </div>
            </div>
          )}

          {/* Add new category */}
          <div className="flex gap-2 items-center">
            <ColorPicker 
              value={newCategory.color} 
              onChange={(color) => setNewCategory({ ...newCategory, color })} 
            />
            <Input
              placeholder={`New ${singularLabel.toLowerCase()} name...`}
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <Button 
              size="sm" 
              onClick={() => handleCreate()} 
              disabled={loading || !newCategory.name.trim()}
              data-testid={`add-${categoryType}-btn`}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Categories list */}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {localCategories.length === 0 && !showTemplates ? (
              <p className="text-center text-muted-foreground text-sm py-4">
                No {label.toLowerCase()} yet. Add one above.
              </p>
            ) : (
              localCategories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 group"
                >
                  {editingId === cat.id ? (
                    <>
                      <ColorPicker 
                        value={editForm.color} 
                        onChange={(color) => setEditForm({ ...editForm, color })} 
                      />
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="flex-1 h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleUpdate(cat.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleUpdate(cat.id)} className="h-8 w-8 p-0">
                        <Check className="w-4 h-4 text-green-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 w-8 p-0">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0 ring-2 ring-offset-1 ring-offset-background" 
                        style={{ backgroundColor: cat.color || "#6b7280", ringColor: cat.color || "#6b7280" }}
                      />
                      <span className="flex-1 text-sm font-medium">{cat.name}</span>
                      {cat.item_count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {cat.item_count} used
                        </Badge>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => startEdit(cat)} className="h-8 w-8 p-0 opacity-50 hover:opacity-100">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 opacity-50 hover:opacity-100 text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(cat.id, cat.name)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Show templates button if they dismissed it but have no categories */}
          {!showTemplates && localCategories.length === 0 && DEFAULT_TEMPLATES[categoryType] && (
            <Button variant="outline" size="sm" onClick={() => setShowTemplates(true)} className="w-full">
              <Sparkles className="w-4 h-4 mr-2" />
              Show Quick Start Templates
            </Button>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
